import React, { useEffect, useRef, useState } from "react";
import { Avatar, Box, Grid, List, ListItem, ListItemAvatar, ListItemSecondaryAction, ListItemText, Paper } from '@mui/material';
import { observer } from "mobx-react-lite";
import { withRouter } from "react-router-dom";
import { LoadingButton, TooltipIconButton } from "../../common/StyledButtons";
import StyledSubHeader from "../../common/StyledSubHeader";
import { isAdmin } from "../../../actions/profile/UserActions";
import { createActivityReportForSite, getAllSitesForAdmin, getAllUsersForAdmin, sendEmailBlastForAdmin } from "../../../actions/internal/AdminActions";
import { getUserId } from "../../../selectors/UserSelectors";
import { NOTIFICATION_TYPE, showNotificationBar } from "../../../actions/error-pages/NotificationBarActions";
import { StyledLoadingSection } from "../../common/StyledProgressCircle";
import StyledAvatarChipsTextField from "../../common/StyledAvatarChipTextField";
import StyledSwitch from "../../common/StyledSwitch";
import StyledPage from "../../common/StyledPage";

const AdminReportsPage = ({ history }) => {

    const formattedSites = useRef();

    const [sites, setSites] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSending, setIsSending] = useState(false);
    const [recipient, setRecipient] = useState({});
    const [options, setOptions] = useState({});
    const [errors, setErrors] = useState([]);

    const loadData = async () => {
        if (!isAdmin()) {
            history.replace('/dashboard');
            return;
        }
        try {
            const userId = getUserId();
            const fetchedSites = await getAllSitesForAdmin(userId);

            formattedSites.current = fetchedSites.sites.map(s => ({
                name: s.site_details.site_name,
                value: s
            }))
            setSites(fetchedSites);
        } catch(err) {
            const e = await err;
            console.error(e);
            showNotificationBar(NOTIFICATION_TYPE.ERROR, 'Uh oh! There was a problem retrieving site information.')
        } finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        loadData();
    }, [])

    const handleRecipientChange = (event, newValue) => {
        setRecipient(newValue)
    };

    const handleOptionChange = (key, value) => {
        const updatedFields = { ...options };
        updatedFields[key] = value;
        setOptions(updatedFields)
    };

    const loadingContent = (
        <StyledLoadingSection />
    )

    if (isLoading) return (loadingContent)

    const validateForm = () => {
        const errs = [];
        if (!recipient || recipient.length === 0) errs.push('recipient');
        return errs;
    }

    const handleSendReport = async () => {
        try {
            setIsSending(true);

            // Verify
            const errs = validateForm();
            setErrors(errs);
            if (errs.length !== 0) {
                return; // If errors exist, return
            }

            // Prepare Payload
            const payloadOptions = {
                is_test: options.is_test !== undefined ? options.is_test : false
            }

            // Send Payload
            await createActivityReportForSite(getUserId(), recipient.value.site_id, payloadOptions);

            showNotificationBar(NOTIFICATION_TYPE.SUCCESS, `Activity report has been successfully queued for ${recipient.value.site_details.site_name}`);
        } catch (ex) {
            console.error(ex);
            showNotificationBar(NOTIFICATION_TYPE.ERROR, "Could not send reports at this time. Please try again in a few moments.");
        } finally {
            setIsSending(false);
        }
        
    }

    // Implement Search and Pagination next
    return (
        <StyledPage>
            <StyledSubHeader title="Reports" />
            <Grid container spacing={2} sx={{ marginTop: '24px' }}>
                <Grid item xs={12}>
                    <Paper variant="outlined" sx={{ padding: '16px'}}>
                        <h4 style={{ margin: 0, fontSize: '24px' }}>Send Activity Report</h4>
                        <p>Select the business which you would like to send an activity report to.</p>
                        <StyledAvatarChipsTextField 
                            error={errors.includes('recipient')}
                            options={formattedSites.current}
                            label="Business"
                            onChange={handleRecipientChange}
                            autocompleteProps={{ multiple: false }}
                        />
                        <h5 style={{ fontSize: '16px', marginBottom: '5px' }}>Options</h5>
                        <Grid item xs={12}>
                            <StyledSwitch 
                                title="Send As Test"
                                onChange={(e) => { handleOptionChange('is_test', e.target.checked) }}
                            />
                        </Grid>
                    </Paper>
                </Grid>
                <Grid item xs={12}>
                    <LoadingButton
                        disabled={isSending}
                        isLoading={isSending}
                        onClick={handleSendReport}
                    >Send Report
                    </LoadingButton>
                </Grid>
            </Grid>
      </StyledPage>
    )
}

export default withRouter(observer(AdminReportsPage));

